/**
 * Comment Item Component
 * Single comment with author, timestamp, and content
 */

import React from 'react';
import { Lock, Bot } from 'lucide-react';
import { CommentResponse } from '@/types';
import { Avatar } from '@/components/common/Avatar';
import { formatRelativeTime } from '@/utils/helpers';

interface CommentItemProps {
  comment: CommentResponse;
}

export function CommentItem({ comment }: CommentItemProps) {
  return (
    <div
      className={`p-4 rounded-lg border ${
        comment.is_internal
          ? 'bg-amber-50 border-amber-200'
          : comment.is_system
          ? 'bg-blue-50 border-blue-200'
          : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {comment.is_system ? (
            <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
          ) : comment.author ? (
            <Avatar
              name={comment.author.name}
              src={comment.author.avatar_url}
              size="md"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-300" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900">
                {comment.is_system
                  ? 'System'
                  : comment.author?.name || 'Unknown User'}
              </span>
              {comment.is_internal && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                  <Lock className="h-3 w-3 mr-1" />
                  Internal
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatRelativeTime(comment.created_at)}
            </span>
          </div>

          <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {comment.content}
          </div>
        </div>
      </div>
    </div>
  );
}
